import { ApiError, NetworkError, type FieldErrors } from '@/api/errors'

/**
 * The v5 envelope (mm-be api/v5/envelope.py). Exactly three shapes:
 *
 *   { data: {...}, meta: null }                    single object
 *   { data: [...], meta: { count, ... } }          list
 *   { error: { code, message, fields } }           failure
 *
 * `data` and `error` are mutually exclusive — never both, never neither.
 * For a single-object endpoint `data` is `null` when empty, never `[]`.
 * `fields` is always present on a failure — `{}` unless it is a validation
 * error — but toApiError tolerates its absence.
 */

/**
 * `count` is the one key ListResponse always sets: the number of rows in THIS
 * response, not a grand total. Anything else is per-endpoint extra that a view
 * merges in.
 *
 * `page` / `page_size` / `total` / `total_pages` are what mm-be's offset-paged
 * lists merge in — movers, watchlist stocks, floorsheet, events. They are
 * declared here by hand because the OpenAPI document types every list's `meta`
 * as `{ count } & Record<string, unknown>`, so `pnpm gen:api` cannot produce
 * them; delete these four once mm-be publishes the shape.
 *
 * `cursor` / `has_more` are what a cursor-paged list is expected to add, so
 * nextCursor reads them when present and otherwise reports the list as
 * exhausted — which is the truth for every v5 list today.
 *
 * A `type`, not an `interface`: the generated schema types `meta` as
 * `{ [key: string]: unknown } | null`, and only a type alias is assignable to
 * an index signature. As an interface, `ok()` bodies would not type-check
 * against openapi-msw's typed handlers.
 */
export type ListMeta = {
  count?: number
  cursor?: string | null
  has_more?: boolean
  page?: number
  page_size?: number
  total?: number
  total_pages?: number
}

export type Meta = ListMeta | null

export interface SuccessEnvelope<T> {
  data: T
  meta: Meta
}

export interface ErrorEnvelope {
  error: { code: string; message: string; fields?: FieldErrors }
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

export const isErrorEnvelope = (body: unknown): body is ErrorEnvelope =>
  isRecord(body) && isRecord(body['error'])

/**
 * Turns a non-2xx body into a typed ApiError.
 *
 * A response that is not the v5 envelope at all (an HTML error page from a
 * proxy, a gateway timeout, or Django's own 500 page — the v5 exception
 * handler hands anything it does not recognise back to Django) becomes an
 * ApiError with a synthesised code rather than crashing on a missing field.
 */
export const toApiError = (body: unknown, status: number): ApiError => {
  if (isErrorEnvelope(body)) {
    const { code, message, fields } = body.error
    return new ApiError({ code, message, fields: fields ?? {}, status })
  }
  return new ApiError({
    code: status >= 500 ? 'INTERNAL_ERROR' : 'NOT_FOUND',
    message: `Request failed with status ${String(status)}.`,
    status,
  })
}

/**
 * Unwraps a success envelope to its `data`.
 *
 * A 204 carries no body, so openapi-fetch hands back an undefined `data`; that
 * is unwrapped to null rather than treated as malformed.
 *
 * `T` names what the caller expects and is asserted, not inferred — the body
 * arrives as `unknown` and this is the one place that claim is made, which is
 * exactly what the rule below normally warns about.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export const unwrap = <T>(body: unknown): T => {
  if (body === undefined || body === null) return null as T
  if (!isRecord(body) || !('data' in body)) {
    throw new NetworkError('Response was not a v5 envelope: missing `data`.')
  }
  return body['data'] as T
}

export const metaOf = (body: unknown): Meta =>
  isRecord(body) && isRecord(body['meta']) ? body['meta'] : null

/**
 * Cursor for the next page, or undefined when the list is exhausted.
 *
 * useInfiniteQuery treats undefined as "no further pages", so a list whose
 * meta carries only `count` — every v5 list today — is a single page.
 */
export const nextCursor = (body: unknown): string | undefined => {
  const meta = metaOf(body)
  if (!meta?.has_more) return undefined
  return meta.cursor ?? undefined
}
